import {
    useCallback,
    useEffect,
    useState,
    FormEventHandler,
    ReactElement,
    useRef
} from 'react';
import {
    UICreatableMultiSelectInput,
    UIFormInput,
    UIFormInputRef,
    UILocationPicker,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISwitch,
    UIText
} from '@/ui';
import { Beacon, BeaconLocation, UpdateBeaconRequest } from '@/api';
import { useUpdateBeaconAction } from '../actions';
import { useDevicesTranslation } from '../hooks';

type UpdateBeaconModalProps = {
    beacon: Beacon;
    availableTags: string[];
    isOpen: boolean;
    onClose: () => void;
};

export function UpdateBeaconModal(props: UpdateBeaconModalProps): ReactElement {
    const { beacon, availableTags, isOpen, onClose } = props;

    const updateBeacon = useUpdateBeaconAction({ beaconId: beacon.id });

    function handleClose() {
        onClose();
    }

    function onSubmit(data: UpdateBeaconRequest) {
        updateBeacon
            .updateBeacon(data)
            .then(() => {
                handleClose();
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {/* Mounted only while open so the fields reset to the beacon's
                current values each time it is reopened. */}
            {isOpen && (
                <FormContent
                    onClose={handleClose}
                    loading={updateBeacon.loading}
                    onSubmit={onSubmit}
                    nameDefault={beacon.name}
                    tagsDefault={beacon.tags}
                    locationDefault={beacon.location}
                    availableTags={availableTags}
                />
            )}
        </UIModal>
    );
}

type FormContentProps = {
    nameDefault: string;
    tagsDefault: string[];
    locationDefault: BeaconLocation | null;
    availableTags: string[];
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: UpdateBeaconRequest) => void;
};

function FormContent(props: FormContentProps): ReactElement {
    const {
        nameDefault,
        tagsDefault: _tagsDefault,
        locationDefault,
        availableTags,
        loading,
        onClose,
        onSubmit
    } = props;
    const { t } = useDevicesTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Name
     */
    const nameRef = useRef<UIFormInputRef>(null);
    const [name, setName] = useState(nameDefault);
    const [nameError, setNameError] = useState('');
    const checkName = useCallback(
        (name: string): string => {
            const nameTrimmed = name.trim();
            if (nameTrimmed.length === 0) {
                return t('shared.fields.name.error_required');
            } else if (nameTrimmed.length > 1000) {
                return t('shared.fields.name.error_too_long');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setNameError(checkName(name));
        }
    }, [name, showRealTimeErrors, checkName]);

    /**
     * Tags
     */
    type TagOption = { value: string; label: string };
    const tagsDefault: TagOption[] = _tagsDefault.map((tag) => ({
        value: tag,
        label: tag
    }));
    const [tags, setTags] = useState<TagOption[]>(tagsDefault);
    const [tagsError, setTagsError] = useState('');
    const tagsOptions: TagOption[] = availableTags.map((tag) => ({
        value: tag,
        label: tag
    }));
    const checkTags = useCallback(
        (tags: TagOption[]): string => {
            if (tags.length > 4) {
                return t('shared.fields.tags.error_too_many');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTagsError(checkTags(tags));
        }
    }, [tags, showRealTimeErrors, checkTags]);

    const [useLocation, setUseLocation] = useState(locationDefault !== null);
    const [location, setLocation] = useState<BeaconLocation | null>(
        locationDefault
    );
    const [locationHasError, setLocationHasError] = useState(false);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkName(name) && !checkTags(tags)) {
            if (useLocation && locationHasError) {
                return;
            }

            setShowRealTimeErrors(false);
            onSubmit?.({
                name,
                tags: tags.map((tag) => tag.value),
                location: useLocation ? location : null
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <UIModalHeader
                    title={t('components.update_beacon_modal.title')}
                />
                <UIModalContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <UIFormInput
                            ref={nameRef}
                            name="name"
                            label={t('shared.fields.name.label')}
                            value={name}
                            description={t('shared.beacons.name_description')}
                            type="text"
                            autoComplete="on"
                            errorText={showRealTimeErrors ? nameError : ''}
                            required
                            maxLength={1000}
                            onChange={setName}
                            autoFocus
                        />

                        <UICreatableMultiSelectInput
                            label={t('shared.fields.tags.label')}
                            placeholder={t('shared.fields.tags.placeholder')}
                            description={t('shared.beacons.tags_description')}
                            required={false}
                            options={tagsOptions}
                            value={tags}
                            onChange={(selectedOptions) => {
                                setTags(selectedOptions as TagOption[]);
                            }}
                            errorText={showRealTimeErrors ? tagsError : ''}
                        />

                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                justifyContent: 'space-between'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t('shared.fields.location.label')}
                                </UIText>
                                <UIText variant="small" type="default2">
                                    {t('shared.beacons.location_description')}
                                </UIText>
                            </div>

                            <UISwitch
                                checked={useLocation}
                                onChange={setUseLocation}
                            />
                        </div>
                        {useLocation && (
                            <UILocationPicker
                                defaultLat={locationDefault?.lat}
                                defaultLng={locationDefault?.lng}
                                showErrors={showRealTimeErrors}
                                onLocationChange={(lat, lng, isValid) => {
                                    setLocation({ lat, lng });
                                    setLocationHasError(!isValid);
                                }}
                            />
                        )}
                    </div>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: t('shared.labels.submit')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            disabled: loading,
                            onClick: onClose
                        }
                    ]}
                />
            </form>
        </>
    );
}
